import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { createUserAccount, setupPaymentMethod } from '../utils/auth';
import { SignupData } from '../types/user';

export default function ExpenseTrackerSignup() {
  const navigate = useNavigate();
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    cardNumber: '',
    cardExpiry: '',
    cardCvc: '',
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsProcessing(true);

    try {
      // Validate form
      if (!formData.email || !formData.password || !formData.firstName || !formData.lastName) {
        throw new Error('Please fill in all required fields');
      }

      if (!formData.cardNumber || !formData.cardExpiry || !formData.cardCvc) {
        throw new Error('Please provide valid payment information');
      }

      // Mock: Setup payment method with Stripe
      const paymentMethodId = await setupPaymentMethod({
        number: formData.cardNumber,
        exp: formData.cardExpiry,
        cvc: formData.cardCvc,
      });

      // Create user account with trial
      const signupData: SignupData = {
        email: formData.email,
        password: formData.password,
        firstName: formData.firstName,
        lastName: formData.lastName,
        paymentMethodId,
      };

      const newUser = createUserAccount(signupData);

      // Success! Redirect to expense tracker app
      navigate('/tools/expense-tracker/app');
    } catch (err: any) {
      setError(err.message || 'Something went wrong. Please try again.');
      setIsProcessing(false);
    }
  };

  return (
    <main className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      <Helmet>
        <title>Start Your Free Trial - Expense Tracker | Free1099Calc</title>
        <meta name="description" content="Start your 14-day free trial. No charge until after your trial ends." />
      </Helmet>

      <div className="mx-auto max-w-4xl px-4 py-12">
        {/* Header */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-block mb-6">
            <div className="text-2xl font-bold text-primary-600">Free1099Calc</div>
          </Link>
          <h1 className="text-4xl font-bold mb-3">Start Your Free Trial</h1>
          <p className="text-lg text-slate-600">
            14 days free, then just $5.99/month
          </p>
        </div>

        {/* Trial Benefits */}
        <div className="mb-8 bg-blue-50 border border-blue-200 rounded-xl p-6">
          <div className="flex items-start gap-4">
            <div className="text-4xl">🎉</div>
            <div>
              <h3 className="font-bold text-lg mb-2">What You Get With Your Trial:</h3>
              <ul className="space-y-2 text-slate-700">
                <li className="flex items-center gap-2">
                  <span className="text-green-600">✓</span>
                  <span>Full access to all features for 14 days</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-green-600">✓</span>
                  <span>Track unlimited expenses during trial</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-green-600">✓</span>
                  <span>Real-time audit risk scoring</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-green-600">✓</span>
                  <span>Cancel anytime during trial - no charge</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-blue-600 font-semibold">→</span>
                  <span className="font-semibold">After trial: $5.99/month, cancel anytime</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Signup Form */}
        <div className="bg-white rounded-2xl shadow-xl border p-8 md:p-10">
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Personal Information */}
            <div>
              <h2 className="text-xl font-bold mb-4">Account Information</h2>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    First Name *
                  </label>
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Last Name *
                  </label>
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    required
                  />
                </div>
              </div>
              <div className="mt-4">
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  required
                />
              </div>
              <div className="mt-4">
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Password *
                </label>
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  required
                  minLength={8}
                />
                <p className="text-sm text-slate-500 mt-1">At least 8 characters</p>
              </div>
            </div>

            {/* Payment Information */}
            <div className="pt-6 border-t">
              <h2 className="text-xl font-bold mb-2">Payment Information</h2>
              <p className="text-sm text-slate-600 mb-4">
                💳 Your card will not be charged during the 14-day trial. After your trial ends, you'll be charged $5.99/month. Cancel anytime.
              </p>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Card Number *
                  </label>
                  <input
                    type="text"
                    name="cardNumber"
                    value={formData.cardNumber}
                    onChange={handleChange}
                    placeholder="4242 4242 4242 4242"
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    required
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Expiry Date *
                    </label>
                    <input
                      type="text"
                      name="cardExpiry"
                      value={formData.cardExpiry}
                      onChange={handleChange}
                      placeholder="MM/YY"
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      CVC *
                    </label>
                    <input
                      type="text"
                      name="cardCvc"
                      value={formData.cardCvc}
                      onChange={handleChange}
                      placeholder="123"
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      required
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Terms and Submit */}
            <div className="pt-6 border-t">
              <p className="text-xs text-slate-600 mb-4">
                By clicking "Start Free Trial", you agree to our{' '}
                <Link to="/terms" className="text-primary-600 hover:underline">
                  Terms of Service
                </Link>{' '}
                and{' '}
                <Link to="/privacy" className="text-primary-600 hover:underline">
                  Privacy Policy
                </Link>
                . You can cancel anytime during your 14-day trial at no charge.
              </p>

              <button
                type="submit"
                disabled={isProcessing}
                className="w-full bg-gradient-to-r from-primary-600 to-blue-600 text-white py-4 px-6 rounded-lg font-semibold text-lg hover:from-primary-700 hover:to-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isProcessing ? 'Processing...' : 'Start Free Trial →'}
              </button>

              <p className="text-center text-sm text-slate-600 mt-4">
                Already have an account?{' '}
                <Link
                  to="/tools/expense-tracker/login"
                  className="text-primary-600 hover:underline font-semibold"
                >
                  Log in
                </Link>
              </p>
            </div>
          </form>
        </div>

        {/* Security Badge */}
        <div className="mt-8 text-center text-sm text-slate-600">
          <div className="flex items-center justify-center gap-2">
            <span>🔒</span>
            <span>Secured by industry-standard encryption</span>
          </div>
        </div>
      </div>
    </main>
  );
}
